import { useEffect, type KeyboardEvent } from 'react';
import { Boxes, LayoutGrid, ListChecks, Package, Receipt, User, Wallet, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PANEL_DE_PESTANAS, PANTALLA_DE_FONDO, idDePestana, vecina, type Pestana, type TipoDePestana } from '@/lib/pestanas';

/**
 * LA BARRA DE PESTAÑAS: lo que se abre queda arriba, como en un navegador.
 * La primera es la pantalla donde se está (no se cierra); después, una por cada cosa abierta.
 * Sin nada abierto no se dibuja. Sin estado propio: se prueba con renderToStaticMarkup.
 *
 * La activa no se distingue solo por color: va en negrita, con una raya arriba y unida al
 * contenido de abajo (tapa la línea de la barra). Con muchas, la barra se desplaza de lado.
 */

const ICONO: Record<TipoDePestana, LucideIcon> = { producto: Package, venta: Receipt, cliente: User, paca: Boxes, gasto: Wallet, asunto: ListChecks };

const FOCO = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500';

export function BarraDePestanas({
  pantalla,
  abiertas,
  activa,
  onActivar,
  onCerrar,
}: {
  /** El nombre de la pantalla donde se está, como lo dice el menú. */
  pantalla: string;
  abiertas: Pestana[];
  activa: string | null;
  onActivar: (clave: string | null) => void;
  onCerrar: (clave: string) => void;
}) {
  // La activa siempre a la vista, aunque haya tantas que la barra se desplace.
  useEffect(() => {
    document.getElementById(idDePestana(activa))?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activa, abiertas.length]);

  if (abiertas.length === 0) return null;

  const conFlechas = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || (e.target as HTMLElement).getAttribute('role') !== 'tab') return;
    e.preventDefault();
    const destino = vecina({ abiertas, activa }, e.key === 'ArrowLeft' ? -1 : 1);
    onActivar(destino);
    document.getElementById(idDePestana(destino))?.focus();
  };

  const pestana = (clave: string | null, titulo: string, Icono: LucideIcon) => {
    const esLaActiva = clave === activa;
    return (
      <div
        key={clave ?? 'pantalla'}
        role="presentation"
        // Clic del medio la cierra, como en un navegador (la de la pantalla no se cierra).
        onMouseDown={(e) => e.button === 1 && e.preventDefault()}
        onAuxClick={(e) => clave !== null && e.button === 1 && onCerrar(clave)}
        className={cn(
          'relative flex shrink-0 items-center rounded-t-lg border border-b-0 text-sm transition-colors',
          esLaActiva
            ? 'border-surface-border bg-surface-muted font-semibold text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50'
            : 'border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
        )}
      >
        {esLaActiva && <span aria-hidden className="absolute inset-x-0 -top-px h-0.5 rounded-t-lg bg-brand-600 dark:bg-brand-400" />}
        <button
          type="button"
          role="tab"
          id={idDePestana(clave)}
          aria-selected={esLaActiva}
          aria-controls={clave === null ? PANTALLA_DE_FONDO : PANEL_DE_PESTANAS}
          tabIndex={esLaActiva ? 0 : -1}
          title={titulo}
          onClick={() => onActivar(clave)}
          className={cn('flex h-11 min-w-0 max-w-[14rem] items-center gap-2 rounded-t-lg pl-3 md:h-9', clave === null ? 'pr-3' : 'pr-1', FOCO)}
        >
          <Icono aria-hidden className={cn('h-4 w-4 shrink-0', esLaActiva ? 'text-brand-600 dark:text-brand-300' : 'text-slate-400')} />
          <span className="truncate">{titulo}</span>
        </button>
        {clave !== null && (
          <button
            type="button"
            aria-label={`Cerrar ${titulo}`}
            title="Cerrar"
            onClick={() => onCerrar(clave)}
            className={cn(
              'mr-1 grid h-11 w-9 shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-700 md:h-6 md:w-6',
              'dark:hover:bg-slate-700 dark:hover:text-slate-100',
              FOCO,
            )}
          >
            <X aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    // La línea de abajo es de este marco y la barra va encima: así la pestaña activa la tapa
    // y queda unida al contenido.
    <div className="relative shrink-0 bg-white after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-surface-border dark:bg-slate-900 dark:after:bg-slate-800">
      <div
        role="tablist"
        aria-label="Lo que tienes abierto"
        onKeyDown={conFlechas}
        // Con la rueda del mouse también se recorre: la barra no enseña su barra de desplazamiento.
        onWheel={(e) => { if (e.deltaY) e.currentTarget.scrollLeft += e.deltaY; }}
        className="relative z-10 flex items-end gap-1 overflow-x-auto px-2 pt-1.5 [scrollbar-width:none] md:px-4 [&::-webkit-scrollbar]:hidden"
      >
        {pestana(null, pantalla, LayoutGrid)}
        {abiertas.map((p) => pestana(p.clave, p.titulo, ICONO[p.tipo]))}
      </div>
    </div>
  );
}
